import { ReactElement, useState, useMemo, memo } from 'react';
import { CircularProgress, dialogClasses } from '@mui/material';

import { Nullable } from '~globals/types/commons';
import { DataPaginateResponse } from '~globals/types/api';
import { useRequest } from '~hooks/useRequest';
import { getTemplateRoutes } from '~services/templateRoute';
import { TemplateRoute } from '~services/templateRoute/types';
import { getWarehouses } from '~services/warehouse';
import { WarehouseExtended, WarehouseHead } from '~services/warehouse/types';
import { DriverCompanyBase } from '~services/driver/types';
import { convertDriverUserToDriverCompanyBase } from '~utils/driver';

import {
  AssistantGenerateRoutesDialogContainer,
  AssistantGenerateRoutesDialogLoadingContainer,
} from '../styles';

import { AssistantGenerateRoutesDialogByTemplateProps } from './types';
import AssistantGenerateRoutesDialogByTemplateProvider from './Context';
import AssistantGenerateRoutesDialogByTemplateContent from './Content';

const AssistantGenerateRoutesDialogByTemplate = ({
  open,
  onClose,
  actions,
  selectedTemplateId,
}: AssistantGenerateRoutesDialogByTemplateProps): ReactElement => {
  const [templates, setTemplates] = useState<TemplateRoute[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<Nullable<TemplateRoute>>(null);
  const [selectedDriver, setSelectedDriver] =
    useState<Nullable<DriverCompanyBase>>(null);

  const [, loadingGetTemplates] = useRequest({
    request: getTemplateRoutes,
    payload: null,
    withPostSuccess: (response) => {
      const currentTemplates = response.data?.data as TemplateRoute[];

      setTemplates(currentTemplates);

      if (selectedTemplateId) {
        const currentSelectedTemplate = currentTemplates.find(
          (template) => template.routeId === selectedTemplateId,
        );

        if (currentSelectedTemplate) {
          setSelectedTemplate(currentSelectedTemplate);

          if (currentSelectedTemplate.driver) {
            setSelectedDriver(
              convertDriverUserToDriverCompanyBase(
                currentSelectedTemplate.driver,
              ),
            );
          }
        }
      }
    },
  });

  const [warehouses, setWarehouses] = useState<WarehouseExtended[]>([]);

  const [, loadingGetWarehouses] = useRequest({
    request: getWarehouses,
    payload: null,
    withPostSuccess: (response) => {
      const {
        data: { results },
      } = response.data as DataPaginateResponse<
        WarehouseExtended[],
        null,
        WarehouseHead
      >;

      setWarehouses(results);
    },
  });

  const loading = useMemo(
    () => loadingGetTemplates || loadingGetWarehouses,
    [loadingGetTemplates, loadingGetWarehouses],
  );

  return (
    <AssistantGenerateRoutesDialogContainer
      open={open}
      sx={{
        [`& .${dialogClasses.paper}`]: {
          minHeight: 'auto',
          maxHeight: 700,
          height: `calc(100% - 64px)`,
        },
      }}
    >
      {loading && (
        <AssistantGenerateRoutesDialogLoadingContainer>
          <CircularProgress color="primary" disableShrink size={80} />
        </AssistantGenerateRoutesDialogLoadingContainer>
      )}

      {!loading && (
        <AssistantGenerateRoutesDialogByTemplateProvider
          value={{
            onClose,
            data: {
              templates,
              selectedTemplate: {
                value: selectedTemplate,
                setValue: setSelectedTemplate,
              },
              selectedDriver: {
                value: selectedDriver,
                setValue: setSelectedDriver,
              },
              warehouses,
            },
            actions,
          }}
        >
          <AssistantGenerateRoutesDialogByTemplateContent />
        </AssistantGenerateRoutesDialogByTemplateProvider>
      )}
    </AssistantGenerateRoutesDialogContainer>
  );
};

export default memo(AssistantGenerateRoutesDialogByTemplate);
